import type { PlanningEntityIdentifier } from 'generated';
import type { EditAppointmentType } from 'RestAPIs/activities/types';
import { useNavParams } from 'hooks';

import AppointmentsPragmatic from './AppointmentsPragmatic';
import TimelineTooltipContainerPragmatic from './TimelineTooltipContainerPragmatic';
import { getItemWidthInPixels } from '../../TimelineView/utils';
import { View } from '../../enums';
import type { PlanningEntity } from '../../types';
import type { ZoomLevel } from '../../TimelineView/TimelineBar/TimelineBar.enums';
import type { Interval } from '../../TimelineView/types';
import { HighlightedRoute } from '../../TimelineView/TimelineRoute/TimelineRoute.styles';
import usePlanningEntity from '../../hooks/usePlanningEntity';

interface Props {
  activeEntityContainingSelection: string | null;
  onChangeActiveEntity?: (entityId: string) => void;
  onChangeSelection?: (selectionIds: string[]) => void;
  timelineRouteWidth: number;
  timeBarItemWidth: number;
  interval: Interval;
  planningEntity: PlanningEntity;
  zoomLevel: ZoomLevel;
  connectAppointmentGrouping: (
    appointmentGroupingEntityId: string,
    editAppointmentType?: EditAppointmentType,
    scheduleStart?: string
  ) => void;
  connectSchedule?: (
    planningEntityIdentifier?: PlanningEntityIdentifier,
    scheduleStart?: string
  ) => void;
}

// Pragmatic port of `TimelineRoute` — wires Pragmatic tooltip + Appointments.
function TimelineRoutePragmatic(props: Props) {
  'use memo';
  const {
    activeEntityContainingSelection,
    connectAppointmentGrouping,
    connectSchedule,
    interval,
    onChangeActiveEntity,
    onChangeSelection,
    planningEntity,
    zoomLevel
  } = props;
  const { planningEntityIdentifier, schedules } = planningEntity;
  const { start } = schedules?.[0] || {};

  const { params } = useNavParams();
  const view = params?.view as View;

  const [planningEntityTypePreference] = usePlanningEntity();
  // TimelinePragmatic shares the entity-type preference with Timeline.
  const planningEntityType =
    planningEntityTypePreference[
      view === View.TimelinePragmatic ? View.Timeline : view
    ];

  const widthInPixels = getItemWidthInPixels(
    props.timeBarItemWidth,
    interval.durationInSeconds,
    zoomLevel
  );

  return (
    <HighlightedRoute
      sx={{
        position: 'relative',
        width: widthInPixels
      }}
      className={`__${planningEntity?.id}__`}
    >
      <TimelineTooltipContainerPragmatic
        planningEntity={planningEntity}
        scheduleStart={start}
        start={interval.start}
        planningEntityIdentifier={planningEntityIdentifier}
        zoomLevel={zoomLevel}
        timeBarItemWidth={props.timeBarItemWidth}
      >
        <AppointmentsPragmatic
          activeEntityContainingSelection={activeEntityContainingSelection}
          connectAppointmentGrouping={connectAppointmentGrouping}
          connectSchedule={connectSchedule}
          planningEntity={planningEntity}
          planningEntityType={planningEntityType}
          interval={interval}
          zoomLevel={zoomLevel}
          timeBarItemWidth={props.timeBarItemWidth}
          onChangeActiveEntity={onChangeActiveEntity}
          onChangeSelection={onChangeSelection}
        />
      </TimelineTooltipContainerPragmatic>
    </HighlightedRoute>
  );
}

export default TimelineRoutePragmatic;
